import { Mail } from "lucide-react"
import type { CatalogItem } from "../types"
import { featureList, formatPrice, includesPremium, requiredPackageCount, savingNet } from "../lib/format"
import { PremiumBadge, SaveBadge, Tick } from "./ui"
import { CONFIG } from "../config"

const CONTACT_EMAIL = "infopakete@sinus-institut.de"

interface Props {
  item: CatalogItem
  catalog: CatalogItem[]
  /** Titel-Override (Staffelkarten heissen im Design "2 Pakete" statt "Bezug von 2 Paketen"). */
  title?: string
  lead?: string
  onAdd: (item: CatalogItem) => void
  /** Bundle/Staffel: oeffnet die Infopaket-Auswahl statt direkt in den Warenkorb. */
  onSelectPackages?: (item: CatalogItem) => void
  featured?: boolean
}

export function ProductCard({ item, catalog, title, lead, onAdd, onSelectPackages, featured }: Props) {
  const onRequest = item.amount_net == null
  const needsSelection = CONFIG.packageSelection && requiredPackageCount(item) > 0
  const saving = savingNet(item, catalog)
  const premium = includesPremium(item)
  const features = featureList(item)

  return (
    <article
      className={`flex flex-col rounded-card border bg-white p-5 shadow-card md:p-7 ${
        featured ? "border-brand-blue ring-1 ring-brand-blue" : "border-line"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-lg font-medium md:text-xl">{title ?? item.name}</h3>
        {premium && <PremiumBadge />}
      </div>

      <p className="mt-2 text-xs text-brand-muted md:min-h-[72px] md:text-sm">
        {lead ?? item.description}
      </p>

      <div className="mt-5 flex flex-wrap items-baseline justify-between gap-3">
        <span className={`font-medium ${onRequest ? "text-lg text-brand-blue md:text-2xl" : "text-xl md:text-3xl"}`}>
          {formatPrice(item.amount_net, item.currency)}
        </span>
        {saving != null && <SaveBadge amount={formatPrice(saving, item.currency)} />}
      </div>

      <ul className="mt-5 flex flex-1 flex-col gap-2.5">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-xs md:text-sm">
            <Tick className="mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {onRequest ? (
        <a
          href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Anfrage: ${item.name}`)}`}
          className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-field border border-brand-blue px-6 py-2.5 text-sm font-medium text-brand-blue transition hover:bg-brand-tint"
        >
          <Mail className="h-4 w-4" />
          Anfragen
        </a>
      ) : (
        <button
          onClick={() => (needsSelection && onSelectPackages ? onSelectPackages(item) : onAdd(item))}
          className="mt-7 w-full rounded-field bg-brand-blue px-6 py-2.5 text-sm font-medium text-white transition hover:brightness-105"
        >
          {needsSelection ? "Pakete wählen" : "In den Warenkorb"}
        </button>
      )}
    </article>
  )
}
